import React, {ReactElement, useCallback, useEffect} from 'react'
import {useQuery} from 'react-query'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'

import {User} from '../../api/user/types'
import {CloseModalConfirmationModal} from '../../components/close-modal-confirmation-modal/close-modal-confirmation-modal'
import {SignModalTitleWithInfo} from '../../components/sign-modal-title-with-info/sign-modal-title-with-info'
import {AuthModal} from '../../modals/auth-flow/auth-modal'
import {auth} from '../../utils/firebase'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {UserService} from '../user'
import {AuthService} from './index'
import {RemoveAccountFailure} from './remove-account-error'

export const useAuthState: (venueId?: string) => {
  isLoggedIn: boolean
  signUp: () => void
  signOut: () => void
  signIn: (email?: string) => void
  removeAccount: (onError?: (failure: RemoveAccountFailure) => void) => void
} = (venueId) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {l} = useLocale()
  const isLoggedIn = useSelector(AuthService.selectors.isLoggedIn())
  const {openModal, closeAllModals, closeModal, updateModalConfig} = useModalActions()
  const setModalTitle = useCallback(
    (title: ReactElement | string) => {
      updateModalConfig('auth-modal', {title})
    },
    [updateModalConfig],
  )
  const handleSignOut = useCallback(() => {
    dispatch(AuthService.actions.signOutRequestAction())
  }, [dispatch])
  const handleRemoveAccount = useCallback(
    (onError?: (failure: RemoveAccountFailure) => void) => {
      dispatch(
        AuthService.actions.removeAccountRequestAction({callback: () => navigate('/remove-account-success'), onError}),
      )
    },
    [dispatch, navigate],
  )
  const handleCloseConfirmation = () => {
    openModal({
      title: l('confirmation'),
      component: <CloseModalConfirmationModal />,
      settings: {
        footer: {
          submitButton: {
            settings: {
              bg: 'primary',
            },
          },
        },
      },
      footerConfig: {
        submitButton: {
          onClick: closeAllModals,
          text: l('confirm'),
        },
        cancelButton: {
          onClick: closeModal,
        },
      },
    })
  }
  const handleSignUp = useCallback(
    (email = '') => {
      openModal({
        id: 'auth-modal',
        title: <SignModalTitleWithInfo />,
        component: (
          <AuthModal
            email={email}
            venueId={venueId}
            setModalTitle={setModalTitle}
          />
        ),
        settings: {modalBody: {maxWidth: '634px'}},
        footerConfig: {disabled: true},
        headerConfig: {type: 'compact'},
        customClose: handleCloseConfirmation,
      })
    },
    [l, venueId, openModal],
  )
  const handleSignIn = useCallback(
    (email = '') => {
      openModal({
        id: 'auth-modal',
        title: <SignModalTitleWithInfo />,
        component: (
          <AuthModal
            email={email}
            venueId={venueId}
            setModalTitle={setModalTitle}
          />
        ),
        settings: {modalBody: {maxWidth: '634px'}},
        footerConfig: {disabled: true},
        headerConfig: {type: 'compact'},
        customClose: handleCloseConfirmation,
      })
    },
    [l, venueId, openModal],
  )
  return {
    isLoggedIn,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    removeAccount: handleRemoveAccount,
  }
}

export const useHandleLoginState: (_params: {
  handleLogIn?: () => void
  handleLogOut?: () => void
  handleLoggedIn?: () => void
  handleLoggedOut?: () => void
  handleUserUpdated?: (_user?: User | null) => void
}) => void = ({handleLogIn, handleLogOut, handleLoggedIn, handleLoggedOut, handleUserUpdated}) => {
  const {isLoggedIn} = useAuthState()
  const isInitialized = useSelector(AuthService.selectors.isInitialized())
  const {data: user} = useQuery(UserService.queries.getUserQuery({params: {id: auth.currentUser?.uid}}))

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    if (isLoggedIn) {
      handleLoggedIn?.()
      return
    }
    handleLoggedOut?.()
  }, [isInitialized, isLoggedIn, handleLoggedOut, handleLoggedIn])

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    if (isLoggedIn) {
      handleLogIn?.()
      return
    }
    handleLogOut?.()
  }, [isInitialized, isLoggedIn, handleLogOut, handleLogIn])

  useEffect(() => {
    handleUserUpdated?.(user)
  }, [user, handleUserUpdated])
}
